import React from 'react';
import { Button, Card, Descriptions, Tag, Typography, Space, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { EmailBindingCard, PasswordSettingsModal } from '../components/AccountSecurity';

const { Title } = Typography;

function Profile() {
  const { user } = useAuth();
  const [passwordOpen, setPasswordOpen] = React.useState(false);

  const getRoleName = (role) => {
    const roleNames = {
      'super_admin': '超级管理员',
      'admin': '管理员',
      'user': '普通用户'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'red',
      'admin': 'orange',
      'user': 'blue'
    };
    return colors[role] || 'default';
  };

  const formatPermissions = (permissions) => {
    const permissionLabels = {
      sites: '站点管理',
      pushRecords: '推送记录',
      groupConfig: '报名处理规则',
      robotSettings: '机器人设置',
      userManagement: '用户管理'
    };

    const actionLabels = {
      read: '查看',
      write: '编辑',
      delete: '删除',
      batch: '批量操作',
      export: '导出'
    };

    const permissionTags = [];
    
    Object.entries(permissions || {}).forEach(([resource, actions]) => {
      const resourceLabel = permissionLabels[resource] || resource;
      Object.entries(actions).forEach(([action, hasPermission]) => {
        if (hasPermission) {
          const actionLabel = actionLabels[action] || action;
          permissionTags.push(
            <Tag key={`${resource}-${action}`} className="permission-tag">
              {resourceLabel}-{actionLabel}
            </Tag>
          );
        }
      });
    });

    return permissionTags;
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2}>个人资料</Title>
        <div className="description">查看和管理您的个人信息</div>
      </div>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Avatar size={80} icon={<UserOutlined />} />
            <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
              {user.username}
            </Title>
            <Tag color={getRoleColor(user.role)} style={{ fontSize: '14px' }}>
              {getRoleName(user.role)}
            </Tag>
          </div>

          <Descriptions
            className="profile-details"
            title="基本信息"
            bordered
            column={{ xs: 1, sm: 1, md: 2 }}
          >
            <Descriptions.Item label="用户名">
              {user.username}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱">
              <Space wrap><span className="security-email">{user.email}</span><Tag color={user.emailVerified ? 'green' : 'orange'}>{user.emailVerified ? '已验证' : '待验证'}</Tag></Space>
            </Descriptions.Item>
            <Descriptions.Item label="角色">
              <Tag color={getRoleColor(user.role)}>
                {getRoleName(user.role)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="账户状态">
              <Tag color={user.isActive ? 'green' : 'red'}>
                {user.isActive ? '正常' : '已禁用'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {user.createdAt ? new Date(user.createdAt).toLocaleString('zh-CN') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {user.lastLogin ? new Date(user.lastLogin).toLocaleString('zh-CN') : '从未登录'}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Title level={4}>权限列表</Title>
            <div style={{ marginTop: 16 }}>
              {user.role === 'super_admin' ? (
                <Tag color="red" style={{ fontSize: '14px' }}>
                  超级管理员 - 拥有所有权限
                </Tag>
              ) : (
                <div>
                  {formatPermissions(user.permissions)}
                  {formatPermissions(user.permissions).length === 0 && (
                    <Tag color="default">暂无权限</Tag>
                  )}
                </div>
              )}
            </div>
          </div>
        </Space>
      </Card>
      <EmailBindingCard />
      <Card className="security-card" title="登录密码">
        <div className="security-setting-row"><Typography.Text type="secondary">定期更新密码；忘记密码时可使用已验证的邮箱找回。</Typography.Text><Button onClick={() => setPasswordOpen(true)}>修改密码</Button></div>
      </Card>
      <PasswordSettingsModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </div>
  );
}

export default Profile;
